import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 0 }}>{children}</Box>}
    </div>
  );
}

// Tone options
const TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "polite", label: "Polite" },
  { value: "casual", label: "Casual" },
  { value: "gentle", label: "Gentle" },
  { value: "formal", label: "Formal" },
];

// Color palette for workflow nodes
const NODE_COLORS = [
  "#10b981",
  "#3b82f6",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#6366f1",
  "#14b8a6",
];

interface WorkflowStep {
  id: string;
  label: string;
  color: string;
}

interface AgentFormData {
  name: string;
  tone: string[];
  fallbackResponse: string;
}

function ManageAgent() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Form state
  const [formData, setFormData] = useState<AgentFormData>({
    name: "",
    tone: [],
    fallbackResponse: "",
  });

  // Workflow state
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { id: "step-1", label: "Start Conversation", color: "#10b981" },
    { id: "step-2", label: "Listen to User", color: "#3b82f6" },
    { id: "step-3", label: "Process Request", color: "#f59e0b" },
    { id: "step-4", label: "Respond", color: "#8b5cf6" },
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Dialog states
  const [openAddStep, setOpenAddStep] = useState(false);
  const [newStepLabel, setNewStepLabel] = useState("");
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
  const [openEditStep, setOpenEditStep] = useState(false);

  // Initialize workflow on mount and when steps change
  React.useEffect(() => {
    const newNodes: Node[] = workflowSteps.map((step, index) => ({
      id: step.id,
      data: { label: step.label },
      position: { x: 150, y: index * 120 + 50 },
      style: {
        background: step.color,
        color: "#fff",
        border: "2px solid rgba(255,255,255,0.3)",
        borderRadius: "8px",
        padding: "10px 15px",
        fontWeight: "600",
        fontSize: "0.9rem",
        minWidth: 140,
        textAlign: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
    }));

    const newEdges: Edge[] = [];
    for (let i = 0; i < workflowSteps.length - 1; i++) {
      newEdges.push({
        id: `edge-${i}`,
        source: workflowSteps[i].id,
        target: workflowSteps[i + 1].id,
        animated: true,
        style: { stroke: "#64748b", strokeWidth: 2 },
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [workflowSteps, setNodes, setEdges]);

  // Handle form changes
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleToneChange = (event: any) => {
    const value = Array.isArray(event.target.value)
      ? event.target.value
      : [event.target.value];
    setFormData((prev) => ({
      ...prev,
      tone: value,
    }));
  };

  const handleFallbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, fallbackResponse: e.target.value }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Workflow management
  const handleAddStep = () => {
    if (newStepLabel.trim()) {
      const newStep: WorkflowStep = {
        id: `step-${Date.now()}`,
        label: newStepLabel,
        color: NODE_COLORS[workflowSteps.length % NODE_COLORS.length],
      };
      setWorkflowSteps([...workflowSteps, newStep]);
      setNewStepLabel("");
      setOpenAddStep(false);
    }
  };

  const handleEditStep = (step: WorkflowStep) => {
    setEditingStep(step);
    setOpenEditStep(true);
  };

  const handleUpdateStep = () => {
    if (editingStep && editingStep.label.trim()) {
      setWorkflowSteps(
        workflowSteps.map((s) => (s.id === editingStep.id ? editingStep : s)),
      );
      setOpenEditStep(false);
      setEditingStep(null);
    }
  };

  const handleDeleteStep = (stepId: string) => {
    if (workflowSteps.length > 1) {
      setWorkflowSteps(workflowSteps.filter((s) => s.id !== stepId));
    } else {
      alert("You must have at least one step in the workflow");
    }
  };

  const handleMoveStep = (index: number, direction: "up" | "down") => {
    const newSteps = [...workflowSteps];
    if (direction === "up" && index > 0) {
      [newSteps[index], newSteps[index - 1]] = [
        newSteps[index - 1],
        newSteps[index],
      ];
    } else if (direction === "down" && index < workflowSteps.length - 1) {
      [newSteps[index], newSteps[index + 1]] = [
        newSteps[index + 1],
        newSteps[index],
      ];
    }
    setWorkflowSteps(newSteps);
  };

  // Handle save
  const handleSave = async () => {
    setIsLoading(true);
    setSuccessMessage("");
    try {
      // Validate form
      if (!formData.name.trim()) {
        alert("Please enter an agent name");
        setIsLoading(false);
        return;
      }

      if (formData.tone.length === 0) {
        alert("Please select at least one tone");
        setIsLoading(false);
        return;
      }

      if (!formData.fallbackResponse.trim()) {
        alert("Please enter a fallback response");
        setIsLoading(false);
        return;
      }

      // Simulate API call
      // console.log("Saving agent with data:", {
      //   ...formData,
      //   workflow: {
      //     steps: workflowSteps,
      //   },
      // });

      // Simulate delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setSuccessMessage("Agent saved successfully!");
      setTimeout(() => {
        navigate("/agent");
      }, 2000);
    } catch (error) {
      console.error("Error saving agent:", error);
      alert("Failed to save agent. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={700}>
              {agentId ? "Edit agent" : "Create agent"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Configure your agent's behavior, tone, and workflow
            </Typography>
          </Stack>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/agent")}
            variant="outlined"
          >
            Back
          </Button>
        </Stack>
      </Paper>

      {successMessage && (
        <Alert severity="success" sx={{ m: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <Card
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="agent management tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: { xs: 2, sm: 3 },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  minHeight: 56,
                },
              }}
            >
              <Tab label="Basic Settings" />
              <Tab label="Personality & Tone" />
              <Tab label="Fallback Response" />
              <Tab label="Workflow" />
            </Tabs>
          </Box>

          {/* Content Area - Full Width */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              px: { xs: 2, sm: 3, md: 4 },
              py: 3,
            }}
          >
            {/* Tab 1: Basic Settings */}
            <TabPanel value={tabValue} index={0}>
              <Stack spacing={3} sx={{ maxWidth: 600 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Agent Name
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    Give your agent a unique and descriptive name
                  </Typography>
                  <TextField
                    fullWidth
                    label="Agent Name"
                    placeholder="e.g., Customer Support Bot"
                    value={formData.name}
                    onChange={handleNameChange}
                    disabled={isLoading}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Box>

                <Divider />

                {/* Info Cards */}
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Agent Information
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: "action.hover",
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Status
                        </Typography>
                        <Chip
                          label={agentId ? "Active" : "Draft"}
                          color={agentId ? "success" : "default"}
                          size="small"
                        />
                      </Stack>
                    </Card>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: "action.hover",
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Created
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date().toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </Card>
                  </Box>
                </Box>
              </Stack>
            </TabPanel>

            {/* Tab 2: Personality & Tone */}
            <TabPanel value={tabValue} index={1}>
              <Stack spacing={3} sx={{ maxWidth: 600 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Agent Tone
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    Select one or more tones that define how your agent
                    communicates
                  </Typography>

                  <FormControl fullWidth sx={{ minHeight: 100 }}>
                    <InputLabel id="tone-select-label">Select Tones</InputLabel>
                    <Select
                      labelId="tone-select-label"
                      id="tone-select"
                      multiple
                      value={formData.tone}
                      onChange={handleToneChange}
                      label="Select Tones"
                      disabled={isLoading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                        },
                      }}
                    >
                      {TONE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      You can select multiple tones to create a balanced
                      personality
                    </FormHelperText>
                  </FormControl>
                </Box>

                {formData.tone.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Selected Tones:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {formData.tone.map((t) => (
                        <Chip
                          key={t}
                          label={
                            TONE_OPTIONS.find((opt) => opt.value === t)
                              ?.label || t
                          }
                          color="primary"
                          variant="filled"
                          size="medium"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Behavior Guidelines
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    {[
                      {
                        title: "Empathy",
                        description: "Shows understanding of user concerns",
                      },
                      {
                        title: "Clarity",
                        description: "Communicates clearly and concisely",
                      },
                      {
                        title: "Proactivity",
                        description: "Anticipates user needs",
                      },
                    ].map((guideline) => (
                      <Card
                        key={guideline.title}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          height: "100%",
                          backgroundColor: "action.hover",
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {guideline.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {guideline.description}
                        </Typography>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Stack>
            </TabPanel>

            {/* Tab 3: Fallback Response */}
            <TabPanel value={tabValue} index={2}>
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Fallback Response
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    What should the agent say when it doesn't know how to
                    respond to a user?
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Fallback Message"
                    placeholder="e.g., 'I appreciate your question. I don't have an answer to that right now...'"
                    value={formData.fallbackResponse}
                    onChange={handleFallbackChange}
                    disabled={isLoading}
                    variant="outlined"
                    helperText={`${formData.fallbackResponse.length}/500 characters`}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Tips for Effective Fallback Responses
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      "Be honest about limitations while maintaining trust",
                      "Offer alternative ways to get help (support contact, FAQ)",
                      "Keep the tone consistent with your agent's personality",
                      "Avoid making promises you can't keep",
                      "Encourage the user to provide more details",
                    ].map((tip, idx) => (
                      <Box key={idx} sx={{ display: "flex", gap: 1.5 }}>
                        <Typography
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            minWidth: 20,
                          }}
                        >
                          •
                        </Typography>
                        <Typography variant="body2">{tip}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </TabPanel>

            {/* Tab 4: Workflow */}
            <TabPanel value={tabValue} index={3}>
              <Stack spacing={3} sx={{ height: "100%" }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Agent Workflow
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    Define the steps your agent takes. Drag to rearrange, edit,
                    or add new steps.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
                    gap: 3,
                    minHeight: 500,
                  }}
                >
                  {/* React Flow Canvas */}
                  <Paper
                    elevation={0}
                    sx={{
                      border: "2px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      backgroundColor: "action.hover",
                    }}
                  >
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                    >
                      <Background />
                      <Controls />
                      <MiniMap />
                    </ReactFlow>
                  </Paper>

                  {/* Workflow Steps Manager */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      overflow: "auto",
                      backgroundColor: "background.paper",
                      height: "fit-content",
                      maxHeight: "600px",
                    }}
                  >
                    <Stack spacing={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAddStep(true)}
                        sx={{ borderRadius: 1 }}
                      >
                        Add Step
                      </Button>

                      <Divider />

                      <Typography variant="subtitle2" fontWeight={600}>
                        Workflow Steps ({workflowSteps.length})
                      </Typography>

                      <Stack spacing={1}>
                        {workflowSteps.map((step, index) => (
                          <Card
                            key={step.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: "action.hover",
                              border: `2px solid ${step.color}`,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: 2,
                                transform: "translateX(2px)",
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minWidth: 0,
                                }}
                              >
                                <DragIndicatorIcon
                                  sx={{
                                    color: "text.disabled",
                                    fontSize: "1.2rem",
                                    flexShrink: 0,
                                  }}
                                />
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: step.color,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontWeight: 500,
                                  }}
                                  title={step.label}
                                >
                                  {step.label}
                                </Typography>
                              </Box>

                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ flexShrink: 0 }}
                              >
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditStep(step)}
                                    sx={{ color: "info.main" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteStep(step.id)}
                                    disabled={workflowSteps.length === 1}
                                    sx={{ color: "error.main" }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>

                            {/* Move buttons */}
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                variant="text"
                                disabled={index === 0}
                                onClick={() => handleMoveStep(index, "up")}
                                sx={{ flex: 1, fontSize: "0.7rem" }}
                              >
                                ↑ Up
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                disabled={index === workflowSteps.length - 1}
                                onClick={() => handleMoveStep(index, "down")}
                                sx={{ flex: 1, fontSize: "0.7rem" }}
                              >
                                ↓ Down
                              </Button>
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Box>
              </Stack>
            </TabPanel>
          </Box>
        </Card>
      </Box>

      {/* Action Buttons - Fixed Footer */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/agent")}
            disabled={isLoading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              isLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
            onClick={handleSave}
            disabled={isLoading}
            size="large"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
            }}
          >
            {isLoading ? "Saving..." : "Save agent"}
          </Button>
        </Stack>
      </Paper>

      {/* Add Step Dialog */}
      <Dialog
        open={openAddStep}
        onClose={() => setOpenAddStep(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Workflow Step</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Step Name"
            placeholder="e.g., Validate Input, Query Database"
            value={newStepLabel}
            onChange={(e) => setNewStepLabel(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddStep()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddStep(false)}>Cancel</Button>
          <Button onClick={handleAddStep} variant="contained">
            Add Step
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Step Dialog */}
      <Dialog
        open={openEditStep}
        onClose={() => setOpenEditStep(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Workflow Step</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editingStep && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Step Name"
                value={editingStep.label}
                onChange={(e) =>
                  setEditingStep({ ...editingStep, label: e.target.value })
                }
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  Step Color
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {NODE_COLORS.map((color) => (
                    <Tooltip key={color} title={color}>
                      <Box
                        onClick={() =>
                          setEditingStep({ ...editingStep, color })
                        }
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: color,
                          cursor: "pointer",
                          border:
                            editingStep.color === color
                              ? "3px solid #000"
                              : "2px solid transparent",
                          transition: "all 0.2s ease",
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditStep(false)}>Cancel</Button>
          <Button onClick={handleUpdateStep} variant="contained">
            Update Step
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManageAgent;
